import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Button from 'shared/components/UC/Button';
import { EvalBoardAssignment } from '../../types/admin';
import styles from './RecordarAppModal.module.css';

interface Props {
  isOpen: boolean;
  applicationNumber: string;
  title: string;
  targets: EvalBoardAssignment[]; // pending | accepted_not_started | in_progress
  onClose: () => void;
  onSent: (count: number) => void;
}

const STATUS_LABELS: Partial<Record<EvalBoardAssignment['status'], string>> = {
  pending: 'Sin respuesta',
  accepted_not_started: 'Sin iniciar',
  in_progress: 'En evaluación',
};

const DEFAULT_MESSAGE =
  'Estimado/a {nombre}, le recordamos que tiene una evaluación pendiente en la plataforma Avanza UC. Por favor ingrese para completarla a la brevedad.';

const RecordarAppModal: React.FC<Props> = ({
  isOpen,
  applicationNumber,
  title,
  targets,
  onClose,
  onSent,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set(targets.map((t) => t.evaluatorId)));
    setMessage(DEFAULT_MESSAGE);
  }, [isOpen, targets]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleSend = async () => {
    if (selected.size === 0) return;
    setSending(true);
    await new Promise((r) => setTimeout(r, 400)); // stub delay
    setSending(false);
    onSent(selected.size);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h2 className="uc-h4">Recordar evaluadores</h2>
            <p className={styles.subtitle}>
              {applicationNumber} · {title}
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Evaluator list */}
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Destinatarios</p>
            <ul className={styles.list}>
              {targets.map((t) => (
                <li key={t.evaluatorId} className={styles.item}>
                  <label className={styles.itemLabel}>
                    <input
                      type="checkbox"
                      checked={selected.has(t.evaluatorId)}
                      onChange={() => toggle(t.evaluatorId)}
                    />
                    <span className={styles.evaluatorName}>{t.evaluatorName}</span>
                    <span className={styles.statusBadge}>
                      {STATUS_LABELS[t.status] ?? t.status}
                    </span>
                    <span className={styles.days}>
                      {Math.round(t.daysSinceAssigned)}&nbsp;días
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          {/* Message */}
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Mensaje (compartido)</p>
            <textarea
              className={styles.messageArea}
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <p className={styles.note}>
              {'{nombre}'} se reemplaza automáticamente por el nombre de cada evaluador al enviar.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={selected.size === 0 || sending}
          >
            {sending
              ? 'Enviando...'
              : `Enviar a ${selected.size}`}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RecordarAppModal;
